// useMfa (contract 06 §9) — wraps AuthMfaFlow. The challenge itself lives in `authStore`, seeded by
// whichever flow issued it (login / signup / resend) and memory-only; this composable owns the code
// input, the 1s display clock, and the gates derived from the server's timestamps.
//
// Every number here is SERVER-authored (BE-AUTH-04): attempts, expiry and the resend cooldown are
// read, never invented. The v3 screen decremented "attempts left" locally and unlocked resend after
// a magic 10s — both fictions are gone (the server's cooldown is its own, currently 60s). When a
// rejection carries no state (the legacy error body ships none) we keep the last known value rather
// than guess: the UI must not assert what it cannot know. Resend failure is surfaced inline — never
// silent.
import { computed, getCurrentScope, onScopeDispose, ref, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import { AuthMfaFlow } from '../flows/AuthMfaFlow'
import { toPostLoginLocation } from '../flows/postLoginNavigation'
import { AuthError } from '../models/auth.error'
import { useAuthStore } from '../stores/authStore'

export type MfaMode = 'login' | 'signup'

export interface UseMfa {
  code: Ref<string>
  hasChallenge: ComputedRef<boolean>
  attemptsRemaining: ComputedRef<number | null>
  expiresAt: ComputedRef<string | null>
  resendAvailableAt: ComputedRef<string | null>
  secondsLeft: ComputedRef<number>
  secondsUntilResend: ComputedRef<number>
  expired: ComputedRef<boolean>
  locked: ComputedRef<boolean>
  canResend: ComputedRef<boolean>
  /** The sign-in announced that this user's authenticator app also answers the code box. Never true
   *  for sign-up, and never for a user without a confirmed app — they see no difference. */
  acceptsAuthenticator: ComputedRef<boolean>
  pending: Ref<boolean>
  error: Ref<string | null>
  verify: () => Promise<void>
  /** Resolves `true` when a replacement challenge landed — the caller clears its inputs on that. */
  resend: () => Promise<boolean>
}

const TICK_MS = 1000

/** Whole seconds from `now` until `iso`, floored at 0; 0 when the server sent no such timestamp. */
function secondsUntil(iso: string | null | undefined, now: number): number {
  if (!iso) return 0
  const at = Date.parse(iso)
  return Number.isNaN(at) ? 0 : Math.max(0, Math.ceil((at - now) / 1000))
}

export function useMfa(mode: MfaMode = 'login'): UseMfa {
  const router = useRouter()
  const store = useAuthStore()
  const flow = new AuthMfaFlow()

  const code = ref('')
  const pending = ref(false)
  const error = ref<string | null>(null)

  const hasChallenge = computed(() => Boolean(store.tempToken && store.mfaChallenge))
  if (!hasChallenge.value) {
    void router.replace({ name: mode === 'signup' ? 'Signup' : 'Login' })
  }

  // A countdown needs a REACTIVE clock: `Date.now()` is not a dependency, so a computed reading it
  // alone would cache and freeze at its first value.
  const now = ref(Date.now())
  const ticker = setInterval(() => (now.value = Date.now()), TICK_MS)
  if (getCurrentScope()) onScopeDispose(() => clearInterval(ticker))

  const attemptsRemaining = computed(() => store.mfaChallenge?.attempts_remaining ?? null)
  const expiresAt = computed(() => store.mfaChallenge?.expires_at ?? null)
  const resendAvailableAt = computed(() => store.mfaChallenge?.resend_available_at ?? null)

  const secondsLeft = computed(() => secondsUntil(expiresAt.value, now.value))
  const secondsUntilResend = computed(() => secondsUntil(resendAvailableAt.value, now.value))

  // Both gates need a challenge to be true: with none, we assert neither expiry nor lockout.
  const expired = computed(
    () =>
      (Boolean(expiresAt.value) && secondsLeft.value === 0) || error.value === 'auth.mfa.expired',
  )
  const locked = computed(
    () =>
      (attemptsRemaining.value !== null && attemptsRemaining.value <= 0) ||
      error.value === 'auth.mfa.locked',
  )
  const canResend = computed(
    () => !pending.value && !locked.value && secondsUntilResend.value === 0,
  )
  const acceptsAuthenticator = computed(
    () => mode === 'login' && Boolean(store.mfaChallenge?.mfa_methods?.includes('totp')),
  )

  /** Fold the server's post-rejection snapshot (`error.details`) into the challenge. Anything the
   *  server omitted keeps its previous value — a missing key is not a zero. */
  function absorbServerState(err: AuthError): void {
    const current = store.mfaChallenge
    if (!current) return
    const attempts = err.details.attempts_remaining
    const expires = err.details.expires_at
    const resendAt = err.details.resend_available_at
    const validAttempts = Number.isInteger(attempts) && (attempts as number) >= 0
    const validExpiry = typeof expires === 'string' && !Number.isNaN(Date.parse(expires))
    const validResend = typeof resendAt === 'string' && !Number.isNaN(Date.parse(resendAt))
    if (!validAttempts && !validExpiry && !validResend) return
    store.setMfaChallenge({
      ...current,
      ...(validAttempts ? { attempts_remaining: attempts as number } : {}),
      ...(validExpiry ? { expires_at: expires as string } : {}),
      ...(validResend ? { resend_available_at: resendAt as string } : {}),
    })
  }

  async function verify(): Promise<void> {
    pending.value = true
    error.value = null
    try {
      await flow.verifyCode(code.value)
      // Signup MFA success → Connections (module-12 Shopify completion is a later wave);
      // login MFA success → the post-login target. The target precedence (next > redirectTarget
      // > Summary) is resolved by AuthLoginFlow and persisted to redirectTarget, so it survives
      // the MFA leg (the /mfa route carries no ?next).
      //
      // ⚠ `SettingsConnections` is module 12's name and it has moved once already: ORP-19897 put the
      // pane under the `/settings` parent and retired the standalone `Connections` record this line
      // used to push. Nothing failed loudly — vue-router threw *inside* `push`, the catch below
      // relabelled it as an unknown auth error, and every signup dead-ended right here under a
      // generic message (ORP-19929). Importing 12's constant would make the string undriftable but
      // drags `useMfa` into the shared `account-*` chunk (~310 KB → ~655 KB on a pre-auth screen),
      // so the name is pinned by test instead: `app/router/__tests__/settingsDestinations.spec.ts`
      // drives THIS call against the real route table. Rename the pane and that spec goes red.
      if (mode === 'signup') {
        await router.push({ name: 'SettingsConnections' })
      } else {
        await router.push(toPostLoginLocation(store.redirectTarget ?? 'Summary'))
      }
    } catch (e) {
      const err = AuthError.from(e)
      absorbServerState(err)
      error.value = err.i18nKey // MFA_INVALID_CODE / MFA_EXPIRED / MFA_LOCKED inline
    } finally {
      pending.value = false
    }
  }

  async function resend(): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      await flow.resendCode() // re-seeds the store with the replacement challenge + temp token
      code.value = ''
      return true
    } catch (e) {
      const err = AuthError.from(e)
      absorbServerState(err)
      error.value =
        err.code === 'mfaExpired' || err.code === 'mfaLocked' || err.code === 'mfaResendThrottled'
          ? err.i18nKey
          : 'auth.mfa.resendFailed'
      return false
    } finally {
      pending.value = false
    }
  }

  return {
    code,
    hasChallenge,
    attemptsRemaining,
    expiresAt,
    resendAvailableAt,
    secondsLeft,
    secondsUntilResend,
    expired,
    locked,
    canResend,
    acceptsAuthenticator,
    pending,
    error,
    verify,
    resend,
  }
}
